/**
 * Read adapters for stored `quadrant` payloads (plots plan O; shapes in
 * docs/deck-design-system/plots/read-adapters.md, stored fixtures in
 * `tests/fixtures/plots/quadrant-*.stored.v1.json`).
 *
 * Shape only: they rename and regroup what Python stamped. No coordinate is
 * computed, rounded or re-placed, and no quadrant is decided here, except in
 * the legacy readers, which keep today's rule for stored shapes that predate
 * a stamp:
 *
 * - `deck-slide: quadrant-map` with `plot`: the payload as built.
 * - `deck-slide: quadrant-map` without `plot` (legacy): points joined to their
 *   rail row by `assetId` (label only when the stored point has no id),
 *   `star/question/niche/risk` renamed to the corner keys, `midpoints` copied,
 *   `quadrantLabels` copied only when all four are present (stored decks carry
 *   `{}`: no corner names). A stored point with no `quadrant` keeps none.
 * - `summary-card: quadrant` with `plot`: the payload as built.
 * - `summary-card: quadrant` without `plot` (legacy): `xLabel/yLabel` to the
 *   axes, the `cornerLabels` tuple to the four named corners as stored, the
 *   stamped `xMidpoint/yMidpoint` when present, else the plane's middle (50),
 *   which is where today's card split; points in stored order, no colour slot.
 * - `quick-study: quadrant` with `quadrant_plot`: the payload as built. A
 *   stored result without it keeps the browser `buildQuadrant` reader in the
 *   app (legacy list item 9).
 */

import { QuadrantPayload } from '@digital-twins/shared-types'

import { QuadrantMapSlideData } from '../../slides/renderer-internal-types'
import { PlotReadAdapter, StoredPlotPayload } from '../contract'
import { cornerLabelsFromTuple } from './cornerLabels'

export type StoredQuadrant = StoredPlotPayload<'quadrant'>
type StoredQuadrantData = StoredQuadrant['data']
type StoredQuadrantPoint = StoredQuadrantData['points'][number]

/** The 0-100 plane's middle: where every map split before the midpoint was stamped. */
export const QUADRANT_PLANE_MIDPOINT = { x: 50, y: 50 } as const

// The deck builder's stored quadrant names, and the camel-case names some
// hand-authored decks carry, as the plot's corner keys (a rename, not a rule).
const STORED_QUADRANT_KEYS: Record<string, string> = {
  star: 'top_right',
  question: 'bottom_right',
  niche: 'top_left',
  risk: 'bottom_left',
  topRight: 'top_right',
  bottomRight: 'bottom_right',
  topLeft: 'top_left',
  bottomLeft: 'bottom_left',
  top_right: 'top_right',
  bottom_right: 'bottom_right',
  top_left: 'top_left',
  bottom_left: 'bottom_left',
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function quadrantPlotOf(value: unknown): StoredQuadrant | null {
  if (!isRecord(value) || value.kind !== 'quadrant' || !isRecord(value.data)) {
    return null
  }
  const data = value.data
  if (!Array.isArray(data.points) || !isRecord(data.axes)) {
    return null
  }
  const labels = isRecord(data.labels) ? data.labels : {}
  return { ...(value as unknown as StoredQuadrant), data: { ...(data as unknown as StoredQuadrantData), labels } }
}

/**
 * A stamped quadrant payload as the kind's stored payload, or null. The deck
 * segment views (W6-G) read a tab's `plot` with it: a live view, so there is
 * no legacy shape behind it.
 */
export function readStampedQuadrant(plot: unknown): StoredQuadrant | null {
  return quadrantPlotOf(plot)
}

// --------------------------------------------------------------------------
// Deck slide

export type StoredQuadrantSlide = QuadrantMapSlideData & { plot?: unknown }

export const quadrantSlidePlotAdapter: PlotReadAdapter<'quadrant', StoredQuadrantSlide> = {
  kind: 'quadrant',
  source: { surface: 'deck-slide', shape: 'quadrant-map' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuadrantSlide {
    return isRecord(stored) && stored.type === 'quadrant-map' && quadrantPlotOf(stored.plot) !== null
  },
  read(stored: StoredQuadrantSlide): StoredQuadrant {
    return quadrantPlotOf(stored.plot) as StoredQuadrant
  },
}

export const quadrantSlideLegacyAdapter: PlotReadAdapter<'quadrant', StoredQuadrantSlide> = {
  kind: 'quadrant',
  source: { surface: 'deck-slide', shape: 'quadrant-map' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredQuadrantSlide {
    return isRecord(stored) && stored.type === 'quadrant-map' && Array.isArray(stored.points)
  },
  read(stored: StoredQuadrantSlide): StoredQuadrant {
    const rail = stored.sidebarItems ?? []
    const railById = new Map<string, (typeof rail)[number]>()
    for (const row of rail) {
      if (row.assetId) {
        railById.set(row.assetId, row)
      }
    }
    const points: Array<StoredQuadrantPoint> = (stored.points ?? []).map((point, index) => {
      const row = point.assetId ? railById.get(point.assetId) : rail[index]
      return {
        id: point.assetId || point.label,
        label: point.label,
        badge: row?.badgeLabel ?? null,
        colorSlot: null,
        x: finiteOrNull(point.x),
        y: finiteOrNull(point.y),
        quadrant: point.quadrant ? STORED_QUADRANT_KEYS[point.quadrant] ?? null : null,
        rank: row?.leaderRank ?? null,
        imageUrl: row?.imageUrl ?? null,
        description: row?.description ?? null,
      }
    })
    const corners = stored.quadrantLabels
    const cornerLabels = corners
      ? cornerLabelsFromTuple([corners.topRight, corners.topLeft, corners.bottomRight, corners.bottomLeft])
      : null
    const data: StoredQuadrantData = {
      points,
      axes: {
        x: { label: stored.axisLabels?.x ?? stored.xAxisLabel ?? '' },
        y: { label: stored.axisLabels?.y ?? stored.yAxisLabel ?? '' },
      },
      midpoint: stored.midpoints ?? { ...QUADRANT_PLANE_MIDPOINT },
      totalCount: points.length,
      labels: {},
    }
    if (cornerLabels) {
      data.cornerLabels = cornerLabels
    }
    return { kind: 'quadrant', data }
  },
}

/** A stored quadrant slide as the kind's payload (plot first, else legacy). */
export function readQuadrantSlide(stored: StoredQuadrantSlide): StoredQuadrant {
  return quadrantSlidePlotAdapter.accepts(stored)
    ? quadrantSlidePlotAdapter.read(stored)
    : quadrantSlideLegacyAdapter.read(stored)
}

// --------------------------------------------------------------------------
// Summary card

export type StoredQuadrantCard = QuadrantPayload & { plot?: unknown }

export const quadrantCardPlotAdapter: PlotReadAdapter<'quadrant', StoredQuadrantCard> = {
  kind: 'quadrant',
  source: { surface: 'summary-card', shape: 'quadrant' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuadrantCard {
    return isRecord(stored) && stored.shape === 'quadrant' && quadrantPlotOf(stored.plot) !== null
  },
  read(stored: StoredQuadrantCard): StoredQuadrant {
    return quadrantPlotOf(stored.plot) as StoredQuadrant
  },
}

export const quadrantCardLegacyAdapter: PlotReadAdapter<'quadrant', StoredQuadrantCard> = {
  kind: 'quadrant',
  source: { surface: 'summary-card', shape: 'quadrant' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredQuadrantCard {
    return isRecord(stored) && stored.shape === 'quadrant' && Array.isArray(stored.points)
  },
  read(stored: StoredQuadrantCard): StoredQuadrant {
    const points: Array<StoredQuadrantPoint> = stored.points.map((point) => ({
      id: point.id,
      label: point.label,
      badge: point.tag ?? null,
      colorSlot: null,
      x: finiteOrNull(point.x),
      y: finiteOrNull(point.y),
      imageUrl: point.imageUrl ?? null,
    }))
    const midpoint =
      typeof stored.xMidpoint === 'number' && typeof stored.yMidpoint === 'number'
        ? { x: stored.xMidpoint, y: stored.yMidpoint }
        : { ...QUADRANT_PLANE_MIDPOINT }
    const data: StoredQuadrantData = {
      points,
      axes: { x: { label: stored.xLabel }, y: { label: stored.yLabel } },
      midpoint,
      totalCount: points.length,
      labels: {},
    }
    const cornerLabels = cornerLabelsFromTuple(stored.cornerLabels)
    if (cornerLabels) {
      data.cornerLabels = cornerLabels
    }
    return { kind: 'quadrant', data }
  },
}

/** A stored summary map card as the kind's payload (plot first, else legacy). */
export function readQuadrantCard(stored: StoredQuadrantCard): StoredQuadrant {
  return quadrantCardPlotAdapter.accepts(stored)
    ? quadrantCardPlotAdapter.read(stored)
    : quadrantCardLegacyAdapter.read(stored)
}

// --------------------------------------------------------------------------
// Quick-study

export interface StoredQuickStudyQuadrant {
  quadrant_plot?: unknown
}

export const quadrantQuickStudyAdapter: PlotReadAdapter<'quadrant', StoredQuickStudyQuadrant> = {
  kind: 'quadrant',
  source: { surface: 'quick-study', shape: 'quadrant' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredQuickStudyQuadrant {
    return isRecord(stored) && quadrantPlotOf(stored.quadrant_plot) !== null
  },
  read(stored: StoredQuickStudyQuadrant): StoredQuadrant {
    return quadrantPlotOf(stored.quadrant_plot) as StoredQuadrant
  },
}

export const QUADRANT_READ_ADAPTERS = [
  quadrantSlidePlotAdapter,
  quadrantSlideLegacyAdapter,
  quadrantCardPlotAdapter,
  quadrantCardLegacyAdapter,
  quadrantQuickStudyAdapter,
] as const
